// Root: fetches dashboard + presets, decides which view to render.

import { useCallback, useEffect, useState } from "react";
import { api, type DashboardResponse } from "./api";
import { aggregate, computeDataAsOf, nowAlignedToHourSec } from "./aggregate";
import { Terminal, type CompanyTopic } from "./views/Terminal";
import { Lab } from "./views/Lab";
import { DebugPanel } from "./components/DebugPanel";
import { COLORS, tStyles } from "./styles";
import type {
  Company,
  FilterPreset,
  QuerySpec,
} from "../shared/types";

type View = "terminal" | "lab";

interface CompaniesData {
  companies: Array<
    Company & {
      specs: Array<QuerySpec & { filter_preset_name: string }>;
    }
  >;
}

export function App() {
  const [view, setView] = useState<View>("terminal");
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [companiesData, setCompaniesData] = useState<CompaniesData | null>(null);
  const [presets, setPresets] = useState<FilterPreset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);

  // Ctrl/Cmd+D toggles debug panel. preventDefault to swallow the browser's
  // bookmark shortcut (acceptable in this admin tool's context).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        setDebugOpen((o) => !o);
      } else if (e.key === "Escape" && debugOpen) {
        setDebugOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [debugOpen]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, cos, pres] = await Promise.all([
        api.dashboard(30),
        api.companies(),
        api.filterPresets(),
      ]);
      setDashboard(dash);
      setCompaniesData(cos);
      setPresets(pres.presets);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (error) {
    return (
      <div style={tStyles.root}>
        <div style={tStyles.emptyBanner}>
          <div style={tStyles.emptyTitle}>STARTUP ERROR</div>
          <div style={{ color: COLORS.down }}>{error}</div>
          <div style={{ marginTop: 12 }}>
            Check that you've run <code>pnpm db:migrate:local</code> and{" "}
            <code>pnpm db:seed:local</code>, and that{" "}
            <code>X_BEARER_TOKEN</code> is set in <code>.dev.vars</code>.
          </div>
          <button style={{ ...tStyles.drillButton, marginTop: 16 }} onClick={refresh}>
            ⟳ retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboard || !companiesData || !presets) {
    return (
      <div style={tStyles.root}>
        <div style={tStyles.emptyBanner}>
          <div style={tStyles.emptyTitle}>LOADING</div>
        </div>
      </div>
    );
  }

  // Anchor metric windows to the latest data we have (MIN across active
  // specs of latest closed bucket), not wall-clock now. dataAsOf is null
  // when D1 has nothing fresh — windowing falls back to wall-clock then,
  // and Terminal renders the "● LIVE" indicator instead of an AS-OF time.
  const dataAsOf = computeDataAsOf(dashboard.items);
  const windowEnd = dataAsOf ?? nowAlignedToHourSec();

  const topics: CompanyTopic[] = dashboard.items.map((it) => ({
    name: it.company.name,
    spec: it.spec,
    buckets: it.buckets,
    agg: aggregate(it.buckets, windowEnd),
  }));

  const main =
    view === "lab" ? (
      <Lab
        companies={companiesData.companies}
        presets={presets}
        onBack={() => setView("terminal")}
        onPresetsChanged={refresh}
      />
    ) : (
      <Terminal
        topics={topics}
        userQueryItems={dashboard.user_query_items ?? []}
        dataAsOf={dataAsOf}
        onRefresh={refresh}
        onOpenLab={() => setView("lab")}
      />
    );

  return (
    <>
      {main}
      <DebugPanel
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        onBackfillDone={refresh}
      />
    </>
  );
}
